'use client';

import { Card, CardContent, cn } from '@up2stay/up2promo-ui';
import { Plus } from 'lucide-react';
import type { KeyboardEvent, ReactNode } from 'react';

const SIZE_TOKENS = {
  /** Profile templates, teams, locations — fills a standard grid slot. */
  default: {
    content: 'min-h-[7.5rem] gap-2 p-5',
    icon: 'h-6 w-6',
  },
  /** Denser grids (e.g. leads). */
  compact: {
    content: 'min-h-[4.5rem] gap-2 p-4',
    icon: 'h-5 w-5',
  },
} as const;

export type GridAddCardProps = {
  label: string;
  onClick: () => void;
  /** Defaults to Plus at the size token. */
  icon?: ReactNode;
  className?: string;
  contentClassName?: string;
  size?: keyof typeof SIZE_TOKENS;
  'data-guidance-target'?: string;
};

/**
 * Dashed placeholder slot for “add another” in equal-height card grids.
 * Status Board: quiet affordance, ink on hover — not a metric tile.
 */
export function GridAddCard({
  label,
  onClick,
  icon,
  className,
  contentClassName,
  size = 'default',
  'data-guidance-target': dataGuidanceTarget,
}: GridAddCardProps) {
  const tokens = SIZE_TOKENS[size];

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <Card
      role="button"
      tabIndex={0}
      className={cn(
        'flex h-full flex-col cursor-pointer border border-dashed border-muted-foreground/30 bg-transparent',
        'transition-colors hover:border-primary hover:bg-primary/5',
        className,
      )}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      data-guidance-target={dataGuidanceTarget}
    >
      <CardContent
        className={cn(
          'flex h-full flex-1 flex-col items-center justify-center text-center text-muted-foreground transition-colors hover:text-primary',
          tokens.content,
          contentClassName,
        )}
      >
        {icon ?? <Plus className={cn(tokens.icon, 'shrink-0')} aria-hidden />}
        <span className="text-sm font-medium leading-tight text-balance">
          {label}
        </span>
      </CardContent>
    </Card>
  );
}
